import React, { useEffect, useState } from "react";
import { materialService } from "../../firebase/services";
import { FileText, Upload, Trash2, Download, Loader2, AlertCircle, CheckCircle2, Plus, X } from "lucide-react";

const formatBytes = (bytes) => {
  if (!bytes || Number.isNaN(Number(bytes))) return "";
  const units = ["B", "KB", "MB", "GB"];
  let n = Number(bytes);
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i += 1; }
  return `${n.toFixed(i ? 1 : 0)} ${units[i]}`;
};

export const AdminMaterials = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", dayNumber: 1, published: true });

  const fetchMaterials = async () => {
    setLoading(true);
    try { setMaterials(await materialService.getMaterials(false)); }
    catch (err) { console.error("Fetch materials error:", err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchMaterials(); }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return alert("Please provide a title.");
    if (!selectedFile) return alert("Please choose a study material file.");
    setUploading(true);
    try {
      await materialService.uploadMaterial({ ...form, file: selectedFile });
      setShowModal(false);
      setForm({ title: "", description: "", dayNumber: 1, published: true });
      setSelectedFile(null);
      await fetchMaterials();
    } catch (err) {
      console.error("Upload material error:", err);
      alert(err.message || "Failed to upload study material.");
    } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this study material record?")) return;
    try { await materialService.deleteMaterial(id); await fetchMaterials(); }
    catch (err) { alert(err.message || "Failed to delete."); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 border-b border-border pb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Study Material Repository</h1>
          <p className="text-xs text-muted-foreground font-mono mt-1">Upload PDF, DOC/DOCX, PPT/PPTX and other supported learning files to Cloudinary.</p>
        </div>
        <button onClick={() => setShowModal(true)} className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-mono font-semibold flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Material
        </button>
      </div>

      {loading ? (
        <div className="p-12 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : materials.length === 0 ? (
        <div className="p-12 text-center text-muted-foreground bg-card border border-border rounded-xl"><AlertCircle className="w-8 h-8 mx-auto mb-2" /><div className="text-sm font-semibold text-foreground">No study material uploaded yet.</div></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {materials.map((m) => (
            <div key={m.id} className="p-5 rounded-xl bg-card border border-border shadow-sm space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 font-mono text-xs border border-emerald-500/20">Day {m.dayNumber}</span>
                  <h3 className="font-bold text-sm mt-2">{m.title}</h3>
                  {m.description && <p className="text-xs text-muted-foreground mt-1">{m.description}</p>}
                </div>
                <span className={`text-[10px] font-mono px-2 py-1 rounded border ${m.published ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-amber-400 border-amber-500/30 bg-amber-500/10"}`}>{m.published ? "Published" : "Draft"}</span>
              </div>
              <div className="border-t border-border pt-3 flex items-center justify-between gap-3">
                <div className="min-w-0"><div className="text-xs font-medium truncate">{m.fileName}</div><div className="text-[10px] text-muted-foreground font-mono">{formatBytes(m.fileSize)}{m.fileFormat ? ` • ${String(m.fileFormat).toUpperCase()}` : ""}</div></div>
                <div className="flex items-center gap-2">
                  <a href={m.fileUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg border border-border hover:bg-muted" title="Open file"><Download className="w-4 h-4" /></a>
                  <button onClick={() => handleDelete(m.id)} className="p-2 rounded-lg border border-border hover:bg-destructive/10 text-destructive" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-card border border-border rounded-2xl shadow-2xl p-6">
            <div className="flex items-center justify-between mb-5"><div><h2 className="font-bold">Upload Study Material</h2><p className="text-xs text-muted-foreground font-mono mt-1">File is uploaded directly to Cloudinary.</p></div><button onClick={() => !uploading && setShowModal(false)}><X className="w-5 h-5" /></button></div>
            <form onSubmit={handleUpload} className="space-y-4">
              <input required value={form.title} onChange={e => setForm({...form,title:e.target.value})} placeholder="Material title" className="w-full px-3 py-2 rounded-lg bg-background border border-input text-sm" />
              <textarea value={form.description} onChange={e => setForm({...form,description:e.target.value})} placeholder="Short description" rows={3} className="w-full px-3 py-2 rounded-lg bg-background border border-input text-sm" />
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs font-mono text-muted-foreground">Day<input type="number" min="1" max="15" value={form.dayNumber} onChange={e => setForm({...form,dayNumber:e.target.value})} className="mt-1 w-full px-3 py-2 rounded-lg bg-background border border-input text-sm text-foreground" /></label>
                <label className="text-xs font-mono text-muted-foreground">File<input type="file" required onChange={e => setSelectedFile(e.target.files?.[0] || null)} className="mt-1 w-full text-xs" accept=".pdf,.doc,.docx,.ppt,.pptx,.txt,.zip,.csv,.xlsx,.xls,.png,.jpg,.jpeg,.webp" /></label>
              </div>
              {selectedFile && <div className="p-3 rounded-lg bg-muted/40 border border-border text-xs flex items-center gap-2"><FileText className="w-4 h-4 text-emerald-400" /><span className="truncate">{selectedFile.name}</span><span className="text-muted-foreground">({formatBytes(selectedFile.size)})</span></div>}
              <label className="flex items-center gap-2 text-xs font-mono"><input type="checkbox" checked={form.published} onChange={e => setForm({...form,published:e.target.checked})} /> Publish to student portal immediately</label>
              <button disabled={uploading} className="w-full py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-semibold text-sm flex items-center justify-center gap-2">{uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4" /> Upload & Save</>}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
